import _get from 'lodash/get'
import _isFinite from 'lodash/isFinite'
import {
  GUEST_DETAIL_REQUIRED,
  QUESTION_TYPE,
  ANSWER_TYPE,
  NOTIFICATION
} from 'constants/additionalQuestion'
import _values from 'lodash/values'
import Reservations from 'api/Reservations'
import _uniqBy from 'lodash/uniqBy'
import Rooms from 'api/Rooms'
import {
  generateBrandLabelFilters,
  preFilledAdditionalQuestion
} from 'utilities/additionalQuestions'
import Vue from 'vue'

const state = {
  isUpdateMode: false,
  isApplyingAfterCoupon: false,
  bookingDetails: {
    rooms: [],
    guest: {} // Should be empty object
  },
  isBookingFromPayment: false,
  reservationDetails: {},
  additionalQuestions: [],
  rawBrandQuestions: null,
  facilityNotifications: [],
  brandNotifications: [],
  guests: [],
  points: {
    selectedSpendingPoint: 0,
    adjustPoint: 0
  },
  updateSetting: {
    is_active: false,
    updatable: false,
    reason: null
  },
  currentStep: 0,
  uploadingAttachments: [] // [promise]
}

const getters = {
  isBookingFromPayment: (state) => state.isBookingFromPayment,
  getBookingDetails: (state) => state.bookingDetails,
  getReservationDetails: (state) => state.reservationDetails,
  getGuest: (state) => state.bookingDetails.guest,
  getFacilityNotifications: (state) => state.facilityNotifications,
  getAdditionalQuestions: (state) => state.additionalQuestions,
  hasAdditionalQuestions: (state) => state.additionalQuestions?.length > 0,
  getBrandQuestions: (state) => state.rawBrandQuestions,
  getGuestsList: (state) => state.guests,
  getPoints: (state) => state.points,
  getCurrentAppliedPoint: (state) =>
    state.points.selectedSpendingPoint < 0
      ? state.points.adjustPoint
      : state.points.selectedSpendingPoint,
  getUpdateSetting: (state) => state.updateSetting,
  isUpdateMode: (state) => state.isUpdateMode,
  isApplyingAfterCoupon: (state) => state.isApplyingAfterCoupon,
  getLimitedPlans: (state, getters, rootState) => {
    if (rootState.setting.init.hotel_site_controller === 'tl_lincon') {
      const plans = (state.reservationDetails?.rooms ?? []).map((room) => {
        return { name: room.room_plan_name, hotel_plan_code: room.hotel_plan_code }
      })
      return _uniqBy(plans, 'hotel_plan_code')
    }
    return []
  },
  getBrandNotifications: (state) => state.brandNotifications,
  getCurrentStep: (state) => state.currentStep,
  waitUploadingAttachment: (state) => {
    return Promise.all(state.uploadingAttachments.map((p) => p.catch(() => {})))
  }
}

const mutations = {
  setIsBookingFromPayment: (state, isBookingFromPayment) => {
    state.isBookingFromPayment = isBookingFromPayment
  },
  setBookingDetails: (state, payload) => {
    state.bookingDetails = payload
  },
  setReservationDetails: (state, payload) => {
    state.reservationDetails = payload
  },
  setGuest: (state, payload) => {
    state.bookingDetails.guest = payload
  },
  setGuestsInRooms: (state, rooms) => {
    state.bookingDetails.rooms = rooms
  },
  setPerRoomPeopleInRooms: (state, { roomIndex, attr, value }) => {
    const questionIndex = state.additionalQuestions.findIndex(
      (question) => question.question_type === QUESTION_TYPE.PEOPLE_PER_ROOM
    )
    state.additionalQuestions[questionIndex].answer.rooms[roomIndex][attr] = value
  },
  setCheckinTime: (state, { value }) => {
    const questionIndex = state.additionalQuestions.findIndex(
      (question) => question.question_type === QUESTION_TYPE.CHECKIN
    )
    state.additionalQuestions[questionIndex].answer.rooms = state.additionalQuestions[
      questionIndex
    ].answer.rooms.map((room) => {
      return {
        ...room,
        checkin_time: value
      }
    })
  },
  setCheckoutTime: (state, { value }) => {
    const questionIndex = state.additionalQuestions.findIndex(
      (question) => question.question_type === QUESTION_TYPE.CHECKOUT
    )
    state.additionalQuestions[questionIndex].answer.rooms = state.additionalQuestions[
      questionIndex
    ].answer.rooms.map((room) => {
      return {
        ...room,
        checkout_time: value
      }
    })
  },
  setQuestionAnswerText: (state, { index, value }) => {
    state.additionalQuestions[index].answer.text = value
  },
  setQuestionAnswer: (state, { index, value }) => {
    state.additionalQuestions[index].answer = value
  },
  setQuestionAttachments: (state, { index, value }) => {
    state.additionalQuestions[index].remote_attachments_urls = value
  },
  setAdditionalQuestions: (state, additionalQuestions) => {
    state.additionalQuestions = [...state.additionalQuestions, ...additionalQuestions]
  },
  setNotifications: (state, notifications) => {
    state.facilityNotifications = notifications
  },
  setGuestsList: (state, payload) => {
    state.guests = payload
  },
  setPoints: (state, payload) => {
    if (payload.selectedSpendingPoint || payload.selectedSpendingPoint === 0)
      state.points.selectedSpendingPoint = payload.selectedSpendingPoint
    if (payload.adjustPoint || payload.adjustPoint === 0)
      state.points.adjustPoint = payload.adjustPoint
  },
  setUpdateSetting: (state, setting) => {
    state.updateSetting = setting
  },
  setUpdateMode: (state, isUpdateMode) => {
    state.isUpdateMode = isUpdateMode
  },
  setApplyingAfterCoupon: (state, isApplyingAfterCoupon) => {
    state.isApplyingAfterCoupon = isApplyingAfterCoupon
  },
  setBrandNotifications: (state, notifications) => {
    state.brandNotifications = notifications
  },
  setBrandQuestions: (state, brandQuestions) => {
    state.additionalQuestions = [...brandQuestions, ...state.additionalQuestions]
  },
  setRawBrandQuestions: (state, brandQuestions) => {
    state.rawBrandQuestions = brandQuestions
  },
  setCurrentStep: (state, payload) => {
    state.currentStep = payload
  },
  resetAdditionalQuestions: (state) => {
    state.additionalQuestions = []
  },
  setUploadingAttachment: (state, job) => state.uploadingAttachments.push(job)
}

const actions = {
  setIsBookingFromPayment: ({ commit }, payload) => {
    commit('setIsBookingFromPayment', payload)
  },
  setBookingDetails: ({ commit }, payload) => {
    commit('setBookingDetails', payload)
  },
  setReservationDetails: ({ commit }, payload) => {
    if (payload.rooms) {
      payload = {
        ...payload,
        rooms: payload.rooms.map((room) => {
          return {
            ...room,
            special_discount: (room.discounts || []).find(
              (discount) => discount.discount_type === 'special'
            )
          }
        })
      }
    }
    commit('setReservationDetails', payload)
  },
  getAdditionalQuestions: ({ dispatch, rootState }, route) => {
    const { params, query } = route
    return Reservations.getAdditionalQuestions(
      {
        reservationID: params.confirmationNumber,
        hotelId: query.code
      },
      {
        bypass_token: query.bypass_token,
        q: JSON.stringify({
          label_filters: [
            { labels: ['place_confirmation_screen'] },
            { labels: ['place_booking_process'] }
          ]
        })
      }
    ).then(({ data }) => {
      data.additional_questions = data.additional_questions.filter((question) => {
        if (question.labels.indexOf('all_users') > -1) return true
        const loggedInUser = rootState.membership.loggedInUser
        if (
          loggedInUser?.id &&
          question.labels.indexOf(`membership_program_${loggedInUser.program_id}`) > -1 &&
          question.labels.indexOf(`membership_rank_${loggedInUser.rank_id}`) > -1
        ) {
          return true
        }
        if (
          loggedInUser.organization &&
          question.labels.indexOf(`membership_program_${loggedInUser.organization.program_id}`) >
            -1 &&
          question.labels.indexOf('membership_organization_all') > -1
        ) {
          return true
        }
        if (
          loggedInUser.organization &&
          question.labels.indexOf(`membership_organization_${loggedInUser.organization.id}`) > -1
        ) {
          return true
        }
        return false
      })
      dispatch('setAdditionalInfo', data)
    })
  },
  getBrandAdditionalQuestions: ({ dispatch, rootGetters, getters, commit }, pageLabel) => {
    const labelFilters = generateBrandLabelFilters(pageLabel, rootGetters)
    return Reservations.getBrandAdditionalQuestions({
      q: JSON.stringify({
        label_filters: labelFilters
      })
    })
      .then((res) => {
        commit('setRawBrandQuestions', res.data)
        dispatch('setBrandNotifications', res.data)
        if (!['place_search_result', 'place_confirmation_screen'].includes(pageLabel)) {
          if (getters.getReservationDetails.email) {
            const additionalQuestions = preFilledAdditionalQuestion(
              res.data,
              getters.getReservationDetails,
              getters.getBookingDetails
            )
            dispatch('setBrandQuestions', additionalQuestions)
          } else {
            dispatch('setBrandQuestions', res.data)
          }
        }
      })
      .catch((e) => {
        console.info(`error ${JSON.stringify(e)}`)
        console.info(`payload ${JSON.stringify(labelFilters)}`)
        Vue.rollbar.info('Brand additonal questions api failed on guest detail page')
      })
  },
  setBrandQuestions: ({ commit }, questions) => {
    let additionalQuestions =
      questions?.filter((question) => {
        return _values(QUESTION_TYPE).includes(question.question_type)
      }) ?? []
    additionalQuestions = additionalQuestions.map((question) => {
      if (question.question_type === QUESTION_TYPE.FREE) {
        return {
          ...question,
          answer: {
            text: null,
            required: question.labels.includes(GUEST_DETAIL_REQUIRED),
            ...question.answer
          }
        }
      }
      return question
    })
    commit('setBrandQuestions', additionalQuestions)
  },
  setAdditionalInfo: ({ commit, rootState }, payload = { additional_questions: [] }) => {
    const notifications = (payload.additional_questions || []).filter((question) => {
      return NOTIFICATION === question.question_type && question.ownable_type !== 'HotelBrand'
    })
    let additionalQuestions =
      payload.additional_questions?.filter((question) => {
        return _values(QUESTION_TYPE).includes(question.question_type)
      }) ?? []
    const rooms =
      payload.rooms ||
      ((state.bookingDetails?.rooms ?? []).length > 0
        ? state.bookingDetails.rooms
        : state.reservationDetails.rooms) ||
      []
    additionalQuestions = additionalQuestions.map((question) => {
      if (question.question_type === QUESTION_TYPE.CHECKIN) {
        return {
          ...question,
          answer: {
            rooms: (question?.answer?.rooms ?? rooms).map((room) => {
              const defaultCheckinTime =
                question && question.labels.includes(GUEST_DETAIL_REQUIRED)
                  ? question.settings.started_at
                  : null
              return {
                reservation_hotel_room_id: room.reservation_hotel_room_id,
                checkin_time:
                  _get(question, ['answer', 'rooms', 0, 'checkin_time']) || defaultCheckinTime
              }
            })
          }
        }
      } else if (question.question_type === QUESTION_TYPE.CHECKOUT) {
        return {
          ...question,
          answer: {
            rooms: (question?.answer?.rooms ?? rooms).map((room) => {
              const defaultCheckoutTime =
                question && question.labels.includes(GUEST_DETAIL_REQUIRED)
                  ? question.settings.started_at
                  : null
              return {
                reservation_hotel_room_id: room.reservation_hotel_room_id,
                checkout_time:
                  _get(question, ['answer', 'rooms', 0, 'checkout_time']) || defaultCheckoutTime
              }
            })
          }
        }
      } else if (question.question_type === QUESTION_TYPE.PEOPLE_PER_ROOM) {
        const required = question.labels.includes(GUEST_DETAIL_REQUIRED)
        return {
          ...question,
          answer: {
            rooms: (question?.answer?.rooms ?? rooms).map((room, roomIndex) => {
              return {
                adults:
                  Number(room.adults) ||
                  room.men_count + room.women_count ||
                  rooms[roomIndex].adults,
                reservation_hotel_room_id: room.reservation_hotel_room_id,
                men_count: _get(
                  question,
                  ['answer', 'rooms', roomIndex, 'men_count'],
                  required ? room.adults : null
                ),
                women_count: _get(
                  question,
                  ['answer', 'rooms', roomIndex, 'women_count'],
                  required ? 0 : null
                )
              }
            })
          }
        }
      } else if (
        question.question_type === QUESTION_TYPE.FREE &&
        question.answer_type === ANSWER_TYPE.TEXT
      ) {
        return {
          ...question,
          answer: {
            text: null,
            required: question.labels.includes(GUEST_DETAIL_REQUIRED),
            ...question.answer
          }
        }
      } else if (
        question.question_type === QUESTION_TYPE.FREE &&
        question.answer_type === ANSWER_TYPE.OPTIONS
      ) {
        return {
          ...question,
          answer: {
            text: null,
            required: question.labels.includes(GUEST_DETAIL_REQUIRED),
            ...question.answer
          }
        }
      }
      return question
    })
    commit('setAdditionalQuestions', additionalQuestions)
    commit('setNotifications', notifications)
  },
  resetAdditionalQuestions({ commit }) {
    commit('resetAdditionalQuestions')
  },
  setGuest: ({ commit }, payload) => {
    commit('setGuest', payload)
  },
  setGuestsInRooms: ({ commit, getters }, guests) => {
    let rooms = getters.getBookingDetails.rooms
    rooms = rooms.map((room, index) => {
      return {
        ...room,
        guest: {
          first_name: guests[index].first_name,
          last_name: guests[index].last_name,
          first_name_kana: guests[index].first_name_kana,
          last_name_kana: guests[index].last_name_kana
        }
      }
    })
    commit('setGuestsInRooms', rooms)
    commit('setGuestsList', guests)
  },
  setPerRoomPeopleInRooms: ({ commit, state }, payload) => {
    commit('setPerRoomPeopleInRooms', payload)
    const otherAttr = payload.attr === 'men_count' ? 'women_count' : 'men_count'
    const rooms =
      (state.bookingDetails?.rooms ?? []).length > 0
        ? state.bookingDetails.rooms
        : state.reservationDetails.rooms
    commit('setPerRoomPeopleInRooms', {
      roomIndex: payload.roomIndex,
      attr: otherAttr,
      value: _isFinite(payload.value)
        ? (rooms[payload.roomIndex]?.adults || payload.value) - payload.value
        : null
    })
  },
  setCheckinTime: ({ commit }, payload) => {
    commit('setCheckinTime', payload)
  },
  setCheckoutTime: ({ commit }, payload) => {
    commit('setCheckoutTime', payload)
  },
  resetPoints({ commit }) {
    commit('setPoints', { selectedSpendingPoint: 0, adjustPoint: 0 })
  },
  setUpdateSetting: ({ commit }, payload) => {
    commit('setUpdateSetting', payload)
  },
  setUpdateMode: ({ commit }, isUpdateMode) => {
    commit('setUpdateMode', isUpdateMode)
    if (!isUpdateMode) {
      commit('setApplyingAfterCoupon', false)
    }
  },
  setApplyingAfterCoupon: ({ commit }, isApplyingAfterCoupon) => {
    commit('setApplyingAfterCoupon', isApplyingAfterCoupon)
  },
  setBrandNotifications: ({ commit, rootState }, questions) => {
    commit(
      'setBrandNotifications',
      questions.filter(
        (question) =>
          question.question_type === NOTIFICATION &&
          (question.labels.includes('target_all_facilities') ||
            question.labels.includes(`target_facility_${rootState.setting.init?.hotel_id}`))
      )
    )
  },
  getBookingPrice: ({ commit, dispatch, getters }, { searchParams }) => {
    return Rooms.getSelectedData(searchParams).then((res) => {
      commit('setBookingDetails', {
        guest: getters.getGuest || {},
        ...res
      })
      dispatch('error/clearReservationError', null, { root: true })
      return res
    })
  },
  setCurrentStep: ({ commit }, step) => {
    commit('setCurrentStep', step)
  }
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}
